import React, { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  filterBy,
  CompositeFilterDescriptor,
} from "@progress/kendo-data-query";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import {
  GridLayout,
  GridLayoutItem,
  TabStrip,
  TabStripSelectEventArguments,
  TabStripTab,
} from "@progress/kendo-react-layout";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import { LoadingPanel } from "../../components/layout/Loading";
import { formatIndianNumber } from "../../_helper/helper";
import {
  clearCurrentPage,
  setCurrentPage,
  setPageLimit,
} from "../pagination/paginationSlice";
import _debounce from "lodash/debounce";
import { TextBox } from "@progress/kendo-react-inputs";
import { filterClearIcon } from "@progress/kendo-svg-icons";
import {
  clearFilterTicketBooking,
  clearTicketBookingList,
  setFilterTicketBooking,
  setTicketBookingTabIndex,
} from "./ticketBookingSlice";
import { getAllTicketBooking } from "./services/ticketBooking.services";
import TabStripTitle from "../../components/common/TabStripTitle";
import { MdCancel, MdCheckCircle } from "react-icons/md";

const TicketBooking: React.FC = () => {
  const dispatch = useAppDispatch();
  const TicketBookingTabIndex = useAppSelector(
    (state) => state.ticketBooking.TicketBookingTabIndex
  );
  const handleSelect = (e: TabStripSelectEventArguments) => {
    dispatch(setTicketBookingTabIndex(e.selected));
    dispatch(clearTicketBookingList());
    dispatch(clearFilterTicketBooking());
  };

  useEffect(() => {
    return () => {
      dispatch(clearCurrentPage());
      dispatch(setCurrentPage(0));
      dispatch(clearFilterTicketBooking());
    };
  }, []);

  return (
    <>
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 0 20px 0",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Ticket Booking List
          </Typography.h4>
        </GridLayoutItem>
        <GridLayoutItem>
          <TabStrip
            selected={TicketBookingTabIndex}
            onSelect={handleSelect}
            className="client-grid"
          >
            <TabStripTab
              title={
                <TabStripTitle
                  title={`Confirmed`}
                  icon={MdCheckCircle}
                  iconStyle={{ color: "green" }}
                />
              }
            >
              <TicketBookingGridComponent />
            </TabStripTab>
            <TabStripTab
              title={
                <TabStripTitle
                  title={`Failed`}
                  icon={MdCancel}
                  iconStyle={{ color: "red" }}
                />
              }
            >
              <TicketBookingGridComponent />
            </TabStripTab>
          </TabStrip>
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

const TicketBookingGridComponent: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const FilterTicketBooking: any = useAppSelector(
    (state) => state.ticketBooking.FilterTicketBooking
  );
  const TicketBookingList = useAppSelector(
    (state) => state.ticketBooking.TicketBookingList
  );
  const TicketBookingTabIndex = useAppSelector(
    (state) => state.ticketBooking.TicketBookingTabIndex
  );
  const loading = useAppSelector((state) => state.ticketBooking.loading);
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);

  const pageChange = (event: GridPageChangeEvent) => {
    const newPage = event.page.skip / event.page.take;
    dispatch(setPageLimit(event.page.take));
    dispatch(setCurrentPage(newPage));
  };

  useEffect(() => {
    const fetchItemList = async () => {
      const payload = {
        ...FilterTicketBooking,
        BookingStatus: TicketBookingTabIndex === 0 ? "CONFIRMED" : "FAILED",
        limit: +pageLimit,
        skip: +currentPage * +pageLimit,
      };
      await dispatch(getAllTicketBooking(payload));
    };
    fetchItemList();
  }, [currentPage, pageLimit]);

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
  };
  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${props.index === 10 ? "locked-header" : ""}`}
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );
  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <KendoGrid
        style={{ maxHeight: "calc(100vh - 220px)" }}
        className="responsive-table"
        filterable={true}
        filter={filter}
        data={filter ? filterBy(TicketBookingList, filter) : TicketBookingList}
        onFilterChange={handleFilterChange}
        total={TicketBookingList[0]?.ItemCount || TicketBookingList?.length}
        skip={currentPage * pageLimit}
        take={pageLimit}
        pageable={{
          buttonCount: 5,
          pageSizes: [5, 10, 50, 100, 300, 500],
          pageSizeValue: pageLimit,
          type: "input",
        }}
        onPageChange={pageChange}
        cells={{
          headerCell: HeaderCustomCell,
          data: MyDataCustomCell,
        }}
      >
        <Column
          field="VisitDate"
          title="Visit Date"
          width={150}
          filterCell={CommonFilterCell}
        />
        <Column
          field="Name"
          title="Name"
          width={200}
          filterCell={CommonFilterCell}
        />
        <Column
          field="MobileNo"
          title="Mobile No."
          width={150}
          filterCell={CommonFilterCell}
        />
        <Column
          field="EmailID"
          title="Email ID"
          width={350}
          filterCell={CommonFilterCell}
        />
        <Column
          field="City"
          title="City"
          width={200}
          filterCell={CommonFilterCell}
        />
        <Column
          field="NumberOfPax"
          title="No. Of Pax"
          width={150}
          cells={{
            data: (props: any) => (
              <td className="text-end">{props.dataItem?.NumberOfPax}</td>
            ),
          }}
        />
        <Column
          field="TotalCost"
          title="Total Cost"
          width={150}
          cells={{
            data: (props: any) => (
              <td className="text-end">
                {props.dataItem?.TotalCost
                  ? formatIndianNumber(props.dataItem?.TotalCost)
                  : ""}
              </td>
            ),
          }}
        />
      </KendoGrid>
    </>
  );
};

const CommonFilterCell = (props: any) => {
  const dispatch = useAppDispatch();
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const FilterTicketBooking: any = useAppSelector(
    (state) => state.ticketBooking.FilterTicketBooking
  );
  const TicketBookingTabIndex = useAppSelector(
    (state) => state.ticketBooking.TicketBookingTabIndex
  );

  const isFilterRef = useRef(true);
  const [inputValue, setInputValue] = useState(props.value || "");
  const [inputField, setInputField] = useState(props.field || "");

  const onChange = (event: any) => {
    setInputValue(event.target.value);
    setInputField(event.target.name);
  };

  const delayedApiCall = React.useCallback(
    _debounce((updatedFilters: any) => {
      dispatch(setCurrentPage(0));
      const payload = {
        ...updatedFilters,
        BookingStatus: TicketBookingTabIndex === 0 ? "CONFIRMED" : "FAILED",
        limit: +pageLimit,
        skip: +currentPage * +pageLimit,
      };

      dispatch(getAllTicketBooking(payload));
    }, 300),
    [dispatch]
  );

  useEffect(() => {
    if (!isFilterRef.current) {
      const updatedFilters = {
        ...FilterTicketBooking,
        [inputField]: inputValue,
      };

      dispatch(setFilterTicketBooking(updatedFilters));
      delayedApiCall(updatedFilters);
    } else {
      isFilterRef.current = false;
    }
    return () => {
      delayedApiCall.cancel();
    };
  }, [inputValue, delayedApiCall]);

  const onClearButtonClick = (event: any) => {
    event.preventDefault();
    props.onChange({
      value: null,
      operator: "",
      syntheticEvent: event,
    });

    const updatedFilters = {
      ...FilterTicketBooking,
      [props.field]: "",
    };

    dispatch(setFilterTicketBooking(updatedFilters));

    const payload = {
      ...updatedFilters,
      BookingStatus: TicketBookingTabIndex === 0 ? "CONFIRMED" : "FAILED",
      limit: +pageLimit,
      skip: +currentPage * +pageLimit,
    };

    dispatch(getAllTicketBooking(payload));
  };

  return (
    <div className="d-flex">
      <TextBox
        value={FilterTicketBooking[props.field] || ""}
        name={props.field || ""}
        onChange={onChange}
      />
      <Button
        svgIcon={filterClearIcon}
        title="Clear"
        disabled={!FilterTicketBooking[props.field]}
        onClick={onClearButtonClick}
        type="button"
        style={{
          marginLeft: "8px",
        }}
      ></Button>
    </div>
  );
};

export default TicketBooking;
